import { useState, useEffect, useMemo, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { collection, getDocs, doc, updateDoc, deleteField, setDoc, query, where, serverTimestamp } from 'firebase/firestore'
import { db, APP_COLLECTION, APP_DOC } from '../lib/firebase'
import { useAuth } from '../context/AuthContext'
import AdminNav from '../components/AdminNav'
import { SEED_COURSES, getModule, publishedModules, requiredModules } from '../data/modules'
import { sheetUrlFor } from '../lib/sheetsCheck'
import { courseStats, averageScore, scoredModuleCount, cohortAverageScore } from '../data/progressStats'
import {
  ArrowLeft,
  Users,
  TrendingUp,
  Award,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowUpDown,
  RotateCcw,
  X,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ShieldOff,
  ExternalLink,
  Check,
  Plus,
  Play,
} from 'lucide-react'

function formatDuration(ms) {
  if (!ms || ms <= 0) return '-'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

function formatDate(timestamp) {
  if (!timestamp) return '-'
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function getStatusBadge(modProgress) {
  if (!modProgress) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Not started</span>
  }
  if (modProgress.completed) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Passed</span>
  }
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">In progress</span>
}

function QuestionModal({ question, onClose }) {
  if (!question) return null

  const hasQuestionData = !!question.questionText

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">
              Question {question.questionIndex + 1}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              question.correct
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-600'
            }`}>
              {question.correct ? 'Correct' : 'Incorrect'} — {question.attempts} attempt{question.attempts !== 1 ? 's' : ''}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {hasQuestionData ? (
            <>
              <h3 className="text-base font-semibold text-gray-800">{question.questionText}</h3>

              <div className="space-y-2">
                {question.options.map((opt, idx) => {
                  const isCorrect = idx === question.correctIndex
                  const wasSelected = question.selectedAnswers?.includes(idx)

                  let style = 'border-gray-200 bg-gray-50 text-gray-400'
                  if (isCorrect) {
                    style = 'border-emerald-400 bg-emerald-50 text-emerald-800'
                  } else if (wasSelected) {
                    style = 'border-red-300 bg-red-50 text-red-700'
                  }

                  return (
                    <div
                      key={idx}
                      className={`rounded-lg border-2 px-4 py-3 text-sm ${style}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="inline-block w-6 text-center font-mono text-sm font-bold opacity-50">
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <span className="flex-1">{opt}</span>
                        {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
                        {wasSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-400 shrink-0" />}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Question data not available. Quiz results saved before this feature was added don&apos;t include question text.
              Reset and retake the quiz to capture full details.
            </p>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default function AdminPage() {
  const { user: viewer } = useAuth()
  const isAdmin = viewer?.isAdmin
  const [usersData, setUsersData] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedRow, setExpandedRow] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState('name')
  const [sortDir, setSortDir] = useState('asc')
  const [questionModal, setQuestionModal] = useState(null)
  // One of the two manager queries failed, so this list is incomplete. Shown,
  // because a silently short list reads as fact.
  const [partialLoad, setPartialLoad] = useState(false)

  async function loadAllUsers() {
    try {
      // A manager asks only for their own team, and firestore.rules requires
      // exactly this constraint of them — an unfiltered read is rejected, not
      // quietly filtered afterwards. So the scoping is enforced twice over: the
      // rules refuse to serve anyone else's document, and the query never asks
      // for one. Admins ask for everybody.
      const usersCol = collection(db, APP_COLLECTION, APP_DOC, 'users')

      // A manager asks twice, because there are two ways to be under them and
      // no single constrained query covers both. `managedBy` is their own team;
      // `managerChain` is everybody further down. Each query satisfies one
      // clause of the read rule on its own, which is what keeps the rest of the
      // roster out of the browser.
      //
      // Both, not just the chain: the chain is derived, so anyone whose chain
      // failed to write would otherwise vanish from their own manager's console
      // (I6). Deduplicated by id — a direct report matches both.
      let userDocs
      if (isAdmin) {
        userDocs = (await getDocs(usersCol)).docs
      } else {
        // allSettled, NOT all. These two queries must fail INDEPENDENTLY or the
        // client throws away the very guarantee the rules preserve: I6 says a
        // stale or missing chain costs a manager their grandchildren and never
        // their own team. Under Promise.all, one rejected chain query discarded
        // the direct-report result with it and rendered an empty console —
        // indistinguishable from "you manage nobody". The concrete way in is
        // the ordinary one: hosting and rules are separate targets, so between
        // the bundle going live and the rules following, the chain query is
        // denied for everyone.
        const [direct, below] = await Promise.allSettled([
          getDocs(query(usersCol, where('managedBy', '==', viewer.uid))),
          getDocs(query(usersCol, where('managerChain', 'array-contains', viewer.uid))),
        ])
        if (direct.status === 'rejected' && below.status === 'rejected') throw direct.reason
        // Say so rather than showing a short list as if it were the whole team.
        setPartialLoad(direct.status === 'rejected' || below.status === 'rejected')
        const byId = new Map()
        for (const settled of [direct, below]) {
          if (settled.status !== 'fulfilled') continue
          for (const d of settled.value.docs) byId.set(d.id, d)
        }
        userDocs = [...byId.values()]
      }
      const users = []

      for (const userDoc of userDocs) {
        const userData = userDoc.data()
        const progressSnap = await getDocs(
          collection(db, APP_COLLECTION, APP_DOC, 'users', userDoc.id, 'progress')
        )
        const progress = {}
        progressSnap.forEach((d) => {
          progress[d.id] = d.data()
        })

        // Both of these come from data/progressStats, which the trainee's own
        // view also uses. They were computed separately here once, and drifted:
        // a trainee whose only completed module was OPTIONAL showed an average
        // score of 88% beside completely empty progress bars, with nothing on
        // screen to explain it.
        const isDone = (m) => progress[String(m.id)]?.completed === true
        const completedCount = requiredModules().filter(isDone).length

        const perCourse = {}
        for (const t of SEED_COURSES) perCourse[t.id] = courseStats(progress, t.id)

        const avgScore = averageScore(progress) ?? 0
        const scoredModules = scoredModuleCount(progress)

        // Find last active date
        let lastActive = userData.lastLoginAt
        Object.values(progress).forEach((p) => {
          const ts = p.completedAt || p.startedAt
          if (ts) {
            const d = ts?.toDate ? ts.toDate() : new Date(ts)
            const la = lastActive?.toDate ? lastActive.toDate() : new Date(lastActive || 0)
            if (d > la) lastActive = ts
          }
        })

        users.push({
          uid: userDoc.id,
          email: userData.email || '',
          displayName: userData.displayName || '',
          photoURL: userData.photoURL || '',
          isAdmin: userData.isAdmin || false,
          role: userData.role || null,
          managedBy: userData.managedBy || null,
          completedCount,
          courseStats: perCourse,
          avgScore,
          scoredModules,
          lastActive,
          progress,
        })
      }

      setUsersData(users)
    } catch (err) {
      console.error('Failed to load admin data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAllUsers()
  }, [])

  const isManager = viewer?.isManager && !isAdmin

  // No client-side scoping here on purpose. Managers are already limited to
  // their own team by the query in loadAllUsers and by firestore.rules; a
  // filter at this point would only make it LOOK enforced, which is precisely
  // the mistake this replaced.
  const filteredAndSorted = useMemo(() => {
    let result = usersData

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (u) =>
          u.displayName.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
      )
    }

    result.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'name':
          cmp = a.displayName.localeCompare(b.displayName)
          break
        case 'completion':
          cmp = a.completedCount - b.completedCount
          break
        case 'score':
          cmp = a.avgScore - b.avgScore
          break
        case 'lastActive': {
          const da = a.lastActive?.toDate ? a.lastActive.toDate() : new Date(a.lastActive || 0)
          const db2 = b.lastActive?.toDate ? b.lastActive.toDate() : new Date(b.lastActive || 0)
          cmp = da.getTime() - db2.getTime()
          break
        }
        default:
          cmp = 0
      }
      return sortDir === 'asc' ? cmp : -cmp
    })

    return result
  }, [usersData, searchQuery, sortField, sortDir])

  // usersData is already only what this viewer may see — their team for a
  // manager, everyone for an admin — so the headline stats need no scoping of
  // their own, and no longer move when someone types in the search box.
  const statsSource = usersData
  const totalUsers = statsSource.length
  const totalRequired = requiredModules().length
  const avgCompletion =
    totalUsers > 0 && totalRequired > 0
      ? Math.round(
          (statsSource.reduce((sum, u) => sum + u.completedCount, 0) /
            (totalUsers * totalRequired)) *
            100
        )
      : 0
  // Averaged over the people who have actually SAT a quiz, not over everybody.
  // The rule lives in progressStats.js so it is tested as a function.
  const { average: avgQuizScore, scored: scoredUsers } = cohortAverageScore(statsSource)

  function toggleSort(field) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  async function handleResetModule(userId, moduleId, moduleTitle) {
    if (!window.confirm(`Reset quiz for "${moduleTitle}"?`)) return
    try {
      const progressRef = doc(db, APP_COLLECTION, APP_DOC, 'users', userId, 'progress', String(moduleId))
      await updateDoc(progressRef, {
        // A reset leaves a marker, exactly as a reissue does. Without one it
        // is invisible: deleting fields tells the trainee's browser nothing,
        // and a reset module looks identical on the server to one somebody is
        // part-way through — both have a startedAt and no results. So a run
        // held in a browser could not tell it had been wiped, and carried on
        // showing answers to a test the console said was never taken.
        resetAt: serverTimestamp(),
        quizState: deleteField(),
        quizResults: deleteField(),
        completed: deleteField(),
        completedAt: deleteField(),
        timeSpentMs: deleteField(),
        // The sheet-check verdict has to go too, or SheetSubmission restores
        // "All checks passed — module complete." from a document whose
        // completion has just been deleted. `submission` is left alone: the
        // trainee's own spreadsheet link is not a claim about their progress,
        // and module 20 reads it back from module 19.
        checkResult: deleteField(),
        // And the answers. "Reset" means they take it again from nothing —
        // leaving the attempt history behind means the drill-down still shows
        // answers to a test the console says they have never taken. A REISSUE
        // keeps it, because there the history is the point.
        attemptHistory: deleteField(),
      })
      await loadAllUsers()
    } catch (err) {
      console.error('Failed to reset module:', err)
      alert('Failed to reset module. See console for details.')
    }
  }

  async function handleResetAll(userId, displayName) {
    if (!window.confirm(`Reset ALL module progress for ${displayName || 'this user'}?`)) return
    try {
      const progressSnap = await getDocs(
        collection(db, APP_COLLECTION, APP_DOC, 'users', userId, 'progress')
      )
      await Promise.all(
        progressSnap.docs.map((d) =>
          updateDoc(doc(db, APP_COLLECTION, APP_DOC, 'users', userId, 'progress', d.id), {
            resetAt: serverTimestamp(),
            quizState: deleteField(),
            quizResults: deleteField(),
            completed: deleteField(),
            completedAt: deleteField(),
            timeSpentMs: deleteField(),
            checkResult: deleteField(),
            attemptHistory: deleteField(),
          })
        )
      )
      await loadAllUsers()
    } catch (err) {
      console.error('Failed to reset all modules:', err)
      alert('Failed to reset all modules. See console for details.')
    }
  }

  async function handleReissue(userId, moduleIds) {
    try {
      await Promise.all(
        moduleIds.map((modId) => {
          const docRef = doc(db, APP_COLLECTION, APP_DOC, 'users', userId, 'progress', String(modId))
          return updateDoc(docRef, {
            reissued: serverTimestamp(),
            completed: deleteField(),
            completedAt: deleteField(),
            timeSpentMs: deleteField(),
            checkResult: deleteField(),
            quizState: deleteField(),
          })
        })
      )
      await loadAllUsers()
    } catch (err) {
      console.error('Failed to reissue modules:', err)
      alert('Failed to reissue. See console for details.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <AdminNav />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {isAdmin ? 'Progress' : 'Team Progress'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isAdmin
            ? 'Track progress and quiz results across all enrolled users'
            : 'Track progress and quiz results for your team'}
        </p>
      </div>

      {/* Summary Cards */}
      {partialLoad && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <strong>This list may be incomplete.</strong> One of the two lookups that finds your
          team was refused, so people further down your line may be missing. Reload in a moment —
          if it persists, tell an admin rather than assuming this is everyone.
        </div>
      )}

      <p className="mb-6 text-sm text-gray-500">
        Progress, scores and resets. Enrolment moved out of this table — degrees are on the{' '}
        <a href="/admin/degrees" className="text-purple-700 hover:underline">Degrees</a> page,
        who confers them on{' '}
        <a href="/admin/departments" className="text-purple-700 hover:underline">Departments</a>,
        and a person's department, role and manager on{' '}
        <a href="/admin/people" className="text-purple-700 hover:underline">People</a>.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">{isManager ? 'Team Members' : 'Enrolled Users'}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalUsers}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Avg Completion</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{avgCompletion}%</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
              <Award className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Avg Quiz Score</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{avgQuizScore}%</p>
          {/* Who the number is actually about, so it cannot be read as a
              cohort-wide figure when most of the cohort has not sat one. */}
          <p className="mt-0.5 text-xs text-gray-400">
            {scoredUsers === 0
              ? 'nobody has sat a quiz yet'
              : `across ${scoredUsers} of ${totalUsers} who have sat one`}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1 hover:text-gray-900">
                    User <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  <button onClick={() => toggleSort('completion')} className="inline-flex items-center gap-1 hover:text-gray-900">
                    Progress <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  <button onClick={() => toggleSort('score')} className="inline-flex items-center gap-1 hover:text-gray-900">
                    Avg Score <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  <button onClick={() => toggleSort('lastActive')} className="inline-flex items-center gap-1 hover:text-gray-900">
                    Last Active <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                {(isAdmin || isManager) && <th className="w-10" />}
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((u, idx) => (
                <UserRow
                  key={u.uid}
                  user={u}
                  index={idx}
                  expanded={expandedRow === u.uid}
                  onToggle={() => setExpandedRow(expandedRow === u.uid ? null : u.uid)}
                  onResetModule={handleResetModule}
                  onResetAll={handleResetAll}
                  onViewQuestion={setQuestionModal}
                  onReissue={handleReissue}
                  viewerIsAdmin={isAdmin}
                  viewerIsManager={isManager}
                />
              ))}
              {filteredAndSorted.length === 0 && (
                <tr>
                  <td colSpan={(isAdmin || isManager) ? 5 : 4} className="px-4 py-12 text-center text-gray-400">
                    {searchQuery ? 'No users match your search' : isManager ? 'No team members assigned yet' : 'No users enrolled yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(isAdmin || isManager) && (
        <AnimatePresence>
          {questionModal && (
            <QuestionModal question={questionModal} onClose={() => setQuestionModal(null)} />
          )}
        </AnimatePresence>
      )}
    </div>
  )
}

function UserRow({ user, index, expanded, onToggle, onResetModule, onResetAll, onViewQuestion, onReissue, viewerIsAdmin, viewerIsManager }) {
  const canExpand = viewerIsAdmin || viewerIsManager
  const [reissueSelection, setReissueSelection] = useState({})

  // One bar per track — a single combined bar hides that someone has finished
  // the whole Finance course while never touching the Sheets one.
  const courseRows = SEED_COURSES
    .filter((t) => requiredModules(t.id).length > 0)
    .map((t) => ({ track: t, stats: user.courseStats?.[t.id] }))
    .filter((r) => r.stats)

  const selectedModuleIds = Object.entries(reissueSelection).filter(([, v]) => v).map(([k]) => parseInt(k, 10))

  const handleReissueSelected = (e) => {
    e.stopPropagation()
    if (selectedModuleIds.length === 0) return
    const names = selectedModuleIds.map((id) => getModule(id)?.title).join(', ')
    if (window.confirm(`Reissue test for: ${names}?`)) {
      onReissue(user.uid, selectedModuleIds)
      setReissueSelection({})
    }
  }

  return (
    <>
      <tr
        className={`border-b border-gray-100 transition-colors ${
          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
        } ${canExpand ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={canExpand ? onToggle : undefined}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName}
                className="w-8 h-8 rounded-full border border-gray-200"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
                {user.displayName?.charAt(0) || '?'}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900 text-sm">{user.displayName || 'Unknown'}</p>
                {user.role === 'admin' && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">Admin</span>
                )}
                {user.role === 'manager' && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">Manager</span>
                )}
              </div>
              <p className="text-xs text-gray-400">{user.email}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="space-y-1.5">
            {courseRows.map(({ track, stats }) => (
              <div key={track.id} className="flex items-center gap-2">
                <span className="text-[10px] text-gray-400 w-28 shrink-0 truncate" title={track.name}>
                  {track.name}
                </span>
                <div className="w-20 h-2 rounded-full bg-gray-100 overflow-hidden shrink-0">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all"
                    style={{ width: `${stats.percent}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap tabular-nums">
                  {stats.completed}/{stats.total}
                  {stats.optionalCompleted > 0 && (
                    <span
                      className="ml-1 text-amber-600"
                      title={`Plus ${stats.optionalCompleted} optional module${stats.optionalCompleted === 1 ? '' : 's'} — done, and scored, but deliberately not counted towards the percentage`}
                    >
                      +{stats.optionalCompleted}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </td>
        <td className="px-4 py-3">
          <span
            className="font-medium text-gray-900"
            title={user.scoredModules
              ? `Across ${user.scoredModules} module${user.scoredModules === 1 ? '' : 's'} they have scored, optional ones included — so this can show a figure while the bars are empty`
              : 'They have not scored anything yet'}
          >
            {user.avgScore > 0 ? `${Math.round(user.avgScore * 100)}%` : '-'}
          </span>
          {user.scoredModules > 0 && user.completedCount === 0 && (
            <p className="text-[10px] text-amber-600">optional only</p>
          )}
        </td>
        <td className="px-4 py-3 text-gray-500 text-xs">
          {formatDate(user.lastActive)}
        </td>
        {canExpand && (
          <td className="px-4 py-3 text-gray-400">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </td>
        )}
      </tr>

      {canExpand && <AnimatePresence>
        {expanded && (
          <tr>
            <td colSpan={5} className="p-0">
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Module Detail
                    </h4>
                    {viewerIsAdmin && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onResetAll(user.uid, user.displayName)
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Reset all modules for this user"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Reset All
                        </button>
                      </div>
                    )}
                  </div>
                  {viewerIsAdmin && selectedModuleIds.length > 0 && (
                    <div className="mb-3 flex items-center gap-2">
                      <button
                        onClick={handleReissueSelected}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        Reissue test for {selectedModuleIds.length} selected module{selectedModuleIds.length !== 1 ? 's' : ''}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setReissueSelection({}) }}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {publishedModules().map((mod, mi, arr) => {
                      const mp = user.progress[String(mod.id)]
                      const isFirstOfCourse = mi === 0 || arr[mi - 1].track !== mod.track
                      return (
                        <Fragment key={mod.id}>
                        {isFirstOfCourse && (
                          <div className="pt-3 first:pt-0 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            {SEED_COURSES.find((t) => t.id === mod.track)?.name || mod.track}
                          </div>
                        )}
                        <div
                          className="flex items-center gap-4 bg-white rounded-lg px-4 py-2.5 border border-gray-100"
                        >
                          {viewerIsAdmin && mp?.quizResults && (
                            <input
                              type="checkbox"
                              checked={!!reissueSelection[mod.id]}
                              onChange={(e) => {
                                e.stopPropagation()
                                setReissueSelection((prev) => ({ ...prev, [mod.id]: !prev[mod.id] }))
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 shrink-0"
                              title="Select for reissue"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <p className="text-sm font-medium text-gray-800 truncate flex-1">
                                {mod.title}
                              </p>
                              {mp?.quizResults?.percentageScore != null && (
                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${
                                  mp.quizResults.percentageScore >= 0.7
                                    ? 'bg-green-100 text-green-700'
                                    : mp.quizResults.percentageScore >= 0.4
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-red-100 text-red-700'
                                }`}>
                                  {Math.round(mp.quizResults.percentageScore * 100)}%
                                </div>
                              )}
                            </div>
                            {/* Sheet modules: the trainee's actual spreadsheet,
                                plus what the auto-check made of it. */}
                            {/* The href is REBUILT from the stored spreadsheet
                                id — never the URL string the trainee typed.
                                Older records hold a raw URL that was accepted
                                without a host check, so linking it would let a
                                trainee choose where an admin's click lands. */}
                            {sheetUrlFor(mp?.submission?.spreadsheetId) && (
                              <div className="mt-2 rounded-lg border border-teal-200 bg-teal-50/40 px-3 py-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <a
                                    href={sheetUrlFor(mp.submission.spreadsheetId)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline"
                                  >
                                    Open their sheet
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                  {mp.checkResult && (
                                    <>
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                          mp.checkResult.passed
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-amber-100 text-amber-700'
                                        }`}
                                      >
                                        {mp.checkResult.passed ? 'Auto-check passed' : 'Auto-check incomplete'}
                                        {' · '}
                                        {mp.checkResult.results
                                          ? `${mp.checkResult.results.filter((c) => c.ok).length}/${mp.checkResult.results.length}`
                                          : `${Math.round((mp.checkResult.score || 0) * 100)}%`}
                                      </span>
                                      {mp.checkResult.checkedAt && (
                                        <span className="text-[10px] text-gray-400">
                                          checked {formatDate(mp.checkResult.checkedAt)}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                                {mp.checkResult?.results?.length > 0 && (
                                  <ul className="mt-1.5 space-y-0.5">
                                    {mp.checkResult.results.map((c, ci) => (
                                      <li key={ci} className="flex items-start gap-1.5 text-[11px]">
                                        <span className={c.ok ? 'text-green-600' : 'text-amber-600'}>
                                          {c.ok ? '✓' : '✗'}
                                        </span>
                                        <span className={c.ok ? 'text-gray-500' : 'text-gray-700'}>
                                          {c.label}
                                          {!c.ok && c.actual != null && (
                                            <span className="text-gray-400"> — found: {String(c.actual)}</span>
                                          )}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                            {mp?.attemptHistory && mp.attemptHistory.length > 1 && (
                              <span className="text-xs text-amber-600 font-medium">
                                {mp.attemptHistory.length} attempts ({mp.attemptHistory.filter(a => !a.passed).length} failed)
                              </span>
                            )}
                            {/* Sequential attempt results */}
                            {mp?.attemptHistory && mp.attemptHistory.length > 0 ? (
                              <div className="mt-2 space-y-3">
                                {mp.attemptHistory.map((attempt, ai) => (
                                  <div key={ai} className={`rounded-lg border p-2 ${attempt.passed ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`}>
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className={`text-[10px] font-bold uppercase tracking-wide ${attempt.passed ? 'text-green-700' : 'text-red-600'}`}>
                                        {ai === 0 ? 'Original' : `Reissue ${ai}`}
                                      </span>
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${attempt.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                        {Math.round(attempt.percentageScore * 100)}%
                                      </span>
                                      {attempt.completedAt && (
                                        <span className="text-[10px] text-gray-400">
                                          {formatDate(attempt.completedAt)}
                                        </span>
                                      )}
                                    </div>
                                    {attempt.questions && attempt.questions.length > 0 && (
                                      <table className="w-full text-xs border-collapse">
                                        <thead>
                                          <tr className="text-left text-gray-400">
                                            <th className="py-0.5 pr-3 font-medium">#</th>
                                            <th className="py-0.5 pr-3 font-medium">Result</th>
                                            <th className="py-0.5 font-medium">Attempts</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {attempt.questions.map((q, qi) => (
                                            <tr key={qi} className="border-t border-gray-100/50">
                                              <td className="py-0.5 pr-3">
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); onViewQuestion({ ...q, questionIndex: qi }) }}
                                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                                >
                                                  Q{qi + 1}
                                                </button>
                                              </td>
                                              <td className="py-0.5 pr-3">
                                                <span className={q.correct ? 'text-green-600' : 'text-red-500'}>
                                                  {q.correct ? 'Correct' : 'Incorrect'}
                                                </span>
                                              </td>
                                              <td className="py-0.5 text-gray-600">{q.attempts}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : mp?.quizResults?.questions && mp.quizResults.questions.length > 0 ? (
                              <table className="mt-2 w-full text-xs border-collapse">
                                <thead>
                                  <tr className="text-left text-gray-400">
                                    <th className="py-1 pr-4 font-medium">#</th>
                                    <th className="py-1 pr-4 font-medium">Result</th>
                                    <th className="py-1 font-medium">Attempts</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {mp.quizResults.questions.map((q, qi) => (
                                    <tr key={qi} className="border-t border-gray-100">
                                      <td className="py-1 pr-4">
                                        {(viewerIsAdmin || viewerIsManager) ? (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); onViewQuestion({ ...q, questionIndex: qi }) }}
                                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                          >
                                            Q{qi + 1}
                                          </button>
                                        ) : (
                                          <span className="text-gray-600 font-medium">Q{qi + 1}</span>
                                        )}
                                      </td>
                                      <td className="py-1 pr-4">
                                        <span className={q.correct ? 'text-green-600' : 'text-red-500'}>
                                          {q.correct ? 'Correct' : 'Incorrect'}
                                        </span>
                                      </td>
                                      <td className="py-1 text-gray-600">{q.attempts}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : null}
                          </div>
                        </div>
                        </Fragment>
                      )
                    })}
                  </div>
                </div>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>}
    </>
  )
}
